import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import './Button.css';

const MotionLink = motion.create(Link);

// How strongly the button is pulled toward the cursor (0–1).
const STRENGTH = 0.35;

/**
 * Button — primary/secondary/ghost variants with the violet→cyan gradient.
 * Adds a magnetic cursor pull + (on primary) a shine sweep on hover.
 *
 * Props:
 *  - variant: 'primary' | 'secondary' | 'ghost' (default 'primary')
 *  - size: 'md' | 'lg' (default 'md')
 *  - to: if set, renders a react-router Link
 *  - href: if set, renders an anchor
 *  - onClick: click handler (renders a button)
 *  - arrow: show trailing arrow (default false)
 *  - magnetic: enable the magnetic cursor pull (default true)
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  to,
  href,
  onClick,
  arrow = false,
  className = '',
  type = 'button',
  magnetic = true,
  ...rest
}) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 250, damping: 15, mass: 0.3 });
  const sy = useSpring(y, { stiffness: 250, damping: 15, mass: 0.3 });

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * STRENGTH);
    y.set((e.clientY - (r.top + r.height / 2)) * STRENGTH);
  };

  const handleLeave = () => {
    x.set(0);
    y.set(0);
  };

  const classes = `tra-btn tra-btn--${variant} tra-btn--${size} ${className}`.trim();

  const motionProps = magnetic
    ? { ref, onMouseMove: handleMove, onMouseLeave: handleLeave, style: { x: sx, y: sy } }
    : {};

  const content = (
    <>
      <span className="tra-btn__label">{children}</span>
      {arrow && <span className="tra-btn__arrow" aria-hidden="true">→</span>}
    </>
  );

  if (to) {
    return (
      <MotionLink to={to} className={classes} {...motionProps} {...rest}>
        {content}
      </MotionLink>
    );
  }

  if (href) {
    return (
      <motion.a href={href} className={classes} {...motionProps} {...rest}>
        {content}
      </motion.a>
    );
  }

  return (
    <motion.button type={type} className={classes} onClick={onClick} {...motionProps} {...rest}>
      {content}
    </motion.button>
  );
};

export default Button;
